import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import AuthClient from '../../../../api/auth/AuthClient';
import { GwDatesWrapper } from '../../../../components/_internal_date_/gwDatesWrapper';
import { getHandoffCartId } from '../../../../store/componentStores/Cart/cart.selectors';
import { getResortLocation } from '../../../../store/componentStores/CurrentLodge/currentLodge.selectors';
import { getIsDaypass } from '../../../../store/componentStores/ManageReservation/manageReservation.selectors';
import { getIsOfferAppliedViaPromoBanner } from '../../../../store/componentStores/Offer/offer.selectors';
import manageReservationTypes from '../../../../store/componentStores/ManageReservation/manageReservation.types';
import offerActionTypes from '../../../../store/componentStores/Offer/offer.types';
import packagesActionTypes from '../../../../store/componentStores/Packages/packages.types';
import reservationsActionTypes from '../../../../store/componentStores/Reservations/reservations.types';
import suiteActionTypes from '../../../../store/componentStores/Suite/suite.types';
import { getStore } from '../../../../store/storeRef';
import { getTheFirstElementOfArray } from '../../../../utilities/arraysUtils';
import {
  CUSTOM_PURCHASE_LOCATIONS,
  EVENT_CATEGORIES,
  GTM_EVENT,
  NO_PROMO_CODE,
  PRODUCT_SCOPES,
  RESERVATION_TYPE
} from '../../../../utilities/constants';
import { pushEvent } from '../../../../utilities/dataLayerUtils';
import { getLodgeDataByCode } from '../../../../utilities/lodgeData';
import { findElementsNotInList } from '../../../../utilities/objectKeysManagementUtils';
import { getReservationCurrency } from '../../../../utilities/reservationsUtils';
import {
  buildImpressionsDataObject,
  buildImpressionsDataObjectPkgs,
  buildImpressionsEventObject,
  buildPackageCartEventObject,
  buildPackagesPurchaseEnhancedECommerceObject,
  buildPurchaseEnhancedECommerceObject,
  buildSuitePurchaseEnhancedECommerceObject,
  getBaseSuiteObject,
  getBirthdayPartySelectObject,
  getDayPassBaseObject,
  getEventBaseObject
} from '../analyticsObjectBuilders';
import { sendCabanasAnalytics, sendPostPurchaseTransaction } from '../analyticsSenders';
import { getAddedPackagesCodes, getAnalyticsPayload, getGroupedAddedPackages } from '../analyticsUtils';
import { getCurrentLocation } from '../utilities/helpers';

export const onPushImpressionsData = ({ eventProps }) => {
  const {
    currentSuite,
    resortLocation,
    checkoutDateSelection,
    checkinDateSelection,
    adultsCount,
    kidsCount,
    position,
    customPurchaseLocation
  } = eventProps;
  const { guests } = getStore().getState();
  const kidsAges = guests.kidsAges;
  const impressionsDataObj = buildImpressionsDataObject(
    kidsAges,
    currentSuite,
    resortLocation,
    checkoutDateSelection,
    checkinDateSelection,
    adultsCount,
    kidsCount,
    position,
    customPurchaseLocation
  );
  const eventData = buildImpressionsEventObject(resortLocation, impressionsDataObj, customPurchaseLocation);

  pushEvent(eventData);
};

onPushImpressionsData.eventType = suiteActionTypes.GTM_SUITE_IMPRESSIONS;

export const onSuiteRatesImpressionsData = ({ eventProps }) => {
  const { currentSuite, customPurchaseLocation } = eventProps;
  const { guests, dates, currentLodge, router, offer } = getStore().getState();
  const resortLocation = currentLodge.resortLocation;
  const kidsCount = guests.kidsCount;
  const adultsCount = guests.adultsCount;
  const kidsAges = guests.kidsAges;
  const checkinDateSelection = dates.checkinDateSelection;
  const checkoutDateSelection = dates.checkoutDateSelection;
  const currentPathLocation = get(router, 'location.pathname', '');
  let allRates = [];
  currentSuite.rateFamilies.forEach(line => {
    return allRates.push(...line.rates);
  });
  const impressionsDataObj = allRates.map(suite => {
    return buildImpressionsDataObject(
      kidsAges,
      currentSuite,
      resortLocation,
      checkoutDateSelection,
      checkinDateSelection,
      adultsCount,
      kidsCount,
      currentPathLocation,
      customPurchaseLocation,
      suite.rate
    );
  });
  const eventData = buildImpressionsEventObject(resortLocation, impressionsDataObj, customPurchaseLocation, offer.code);

  pushEvent(eventData);
};

onSuiteRatesImpressionsData.eventType = suiteActionTypes.GTM_SUITE_RATES_IMPRESSIONS;

export const onPkgsImpressionsData = ({ eventProps }) => {
  const {
    pkgs,
    resortLocation,
    checkoutDateSelection,
    checkinDateSelection,
    adultsCount,
    kidsCount,
    customPurchaseLocation,
    upsellRecommendationData
  } = eventProps;

  if (!pkgs || pkgs.length === 0) {
    return;
  }

  const { router } = getStore().getState();
  const currentPathLocation = get(router, 'location.pathname', '');
  const purchaseLocation = getCurrentLocation(currentPathLocation);

  const impressionProducts = pkgs.map((pkg, index) => {
    return buildImpressionsDataObjectPkgs(
      pkg,
      resortLocation,
      checkoutDateSelection,
      checkinDateSelection,
      adultsCount,
      kidsCount,
      index,
      customPurchaseLocation ?? purchaseLocation,
      'package',
      'package',
      upsellRecommendationData
    );
  });

  const eventData = buildImpressionsEventObject(
    resortLocation,
    impressionProducts,
    customPurchaseLocation ?? purchaseLocation
  );

  pushEvent(eventData);
};

onPkgsImpressionsData.eventType = packagesActionTypes.GTM_PACKAGES_IMPRESSIONS;

const handlePackagePurchaseTrackingAction = pckCode => {
  return `${pckCode} Pass Purchased`;
};

/**
 * Listener on purchase event
 * @param {Object} event
 */
export const onReservationSuccess = event => {
  const UUID = uuidv4();
  const { suite } = getStore().getState();
  const { reservation, packages, suiteContent, upsellRecommendationData, offerCode } = event.response;
  const { suiteCategory } = suiteContent;
  if (!reservation) return;
  const voyagerPointsDiscount = reservation.costSummary.totalPoints;
  const offersPointsDiscount = reservation.costSummary.totalOffers;
  const packagesResponse = packages ?? [];
  pushSuiteCheckoutSuccessData(
    reservation,
    packagesResponse,
    packages,
    voyagerPointsDiscount,
    offersPointsDiscount,
    suite.dayType,
    suiteContent,
    UUID,
    upsellRecommendationData,
    offerCode
  );
  pushReservationData(reservation, packagesResponse, packages, suite.dayType, suiteCategory, UUID);
  pushInteractionsData(reservation, packagesResponse);
  // In order to save this event in the history (so we can log it) we should return it!
  // otherwise it will be ignored!

  const passPackagesCodes = packages.filter(pck => pck.packageType === 'Pass').map(pck => pck.packageCode);
  const fullPassPackages = packagesResponse.filter(pck => passPackagesCodes.includes(pck.packageCode));

  fullPassPackages.forEach(pck => {
    pushEvent({
      event: GTM_EVENT.NAME.passPurchase,
      category: EVENT_CATEGORIES.GTM.passPackagePurchasesVariationB,
      action: handlePackagePurchaseTrackingAction(pck.packageCode),
      label: `Revenue: ${pck.packageAmount}`
    });
  });

  return event;
};

onReservationSuccess.eventType = reservationsActionTypes.GTM_RESERVATION_NEW_OR_DAYPASS; // Redux Action Type. Get it from the store

/**
 * Listener triggered on post package added, currently on CMP
 * @param {Object} event Event object triggered by redux
 * @returns Object -- event
 */
export const onPackageAdded = event => {
  const UUID = uuidv4();
  const { response } = event;
  const { router, suite, entities } = getStore().getState();
  const { manageReservationDetails } = entities;
  const upsellRecommendationData = manageReservationDetails?.recommendedAddOns;

  if (!response) return;

  const packages = get(response, 'packages', []);
  const cabanas = get(response, 'cabanas', []);
  const reservation = get(response, 'reservation', {});
  const maxOccupancy = get(response, 'maxOccupancy', 0) ?? manageReservationDetails?.data?.product?.maxOccupancy;
  const customPurchaseLocation = get(response, 'customPurchaseLocation', null);

  const currentPathLocation = get(router, 'location.pathname', '');
  const isDaypass = getIsDaypass(manageReservationDetails);
  const purchaseLocation = customPurchaseLocation
    ? customPurchaseLocation
    : getCurrentLocation(currentPathLocation, isDaypass);

  if (!isEmpty(packages) && !isEmpty(reservation)) {
    sendPostPurchaseTransaction(
      packages,
      reservation,
      purchaseLocation,
      suite.dayType,
      maxOccupancy,
      upsellRecommendationData
    );
  }

  if (!isEmpty(cabanas)) {
    if (!isEmpty(reservation)) {
      sendCabanasAnalytics(cabanas, reservation, suite.dayType, UUID);
    }
  }
};

onPackageAdded.eventType = manageReservationTypes.GTM_POST_RESERVATION_DETAILS_SALE_PACKAGE_ADDED;

const pushInteractionsData = (reservation, packages) => {
  const isDayPass = reservation.reservationType.includes('PASS');
  const interactionsData = {
    event: EVENT_CATEGORIES.INTERACTIONS.postReservation,
    reservationNumber: reservation.id ? reservation.id : reservation.externalId,
    reservationTotal: reservation.costSummary.total,
    roomCode: reservation.product.code,
    roomQuantity: reservation.guests.adults + reservation.guests.children.length,
    roomPrice: !isDayPass
      ? reservation.product.totalPrice.toFixed(2)
      : (reservation.product.totalPrice / reservation.guests.quantity).toFixed(2),
    packages:
      packages && packages.length > 0
        ? packages.map(pkg => ({
            packageCode: pkg.code,
            packageQuantity: pkg.quantity,
            packagePrice: pkg.unitPrice
          }))
        : null
  };
  pushEvent(interactionsData);
};

const pushReservationData = (reservation, reservationPackages, packagesContent, dayType, suiteCategory, UUID) => {
  const authClient = new AuthClient();
  const isUserLoggedIn = authClient.isLoggedIn();
  const numberOfNights = GwDatesWrapper.diff(reservation.departure, reservation.arrival, 'days');
  const isDayPass = reservation.reservationType.includes('PASS');
  const averageRate = !isDayPass
    ? reservation.product.totalPrice / numberOfNights
    : reservation.product.totalPrice / reservation.guests.quantity;

  const { packages, packageTotalRevenue } = reservationPackages.reduce(
    (packagesAcc, reservationPackage) => {
      if (reservationPackage) {
        const foundPackage = packagesContent.find(
          pkg => pkg.packageCode === reservationPackage.packageCode || pkg.code === reservationPackage.code
        );
        packagesAcc.packages.push({
          name: reservationPackage?.name || reservationPackage?.description,
          id: reservationPackage?.code || reservationPackage?.packageCode,
          variant: foundPackage ? foundPackage.type || foundPackage.packageType : '',
          brand: 'great wolf',
          price: reservationPackage?.unitPrice || reservationPackage?.unitAmount,
          quantity: reservationPackage.quantity,
          category: 'packages',
          coupon: reservation.offerCode
        });

        packagesAcc.packageTotalRevenue +=
          reservationPackage.quantity * (reservationPackage?.unitPrice || reservationPackage?.unitAmount);
      }
      return packagesAcc;
    },
    { packages: [], packageTotalRevenue: 0 }
  );

  // 1. Push data - track reservation id
  pushEitherSuiteOrDayPass(reservation, suiteCategory);

  // 2. Track checkout step 1 for checkout and 2 for confirmation
  const searchCode = reservation?.offerCode || reservation?.rateCode;
  const optionCode = searchCode !== NO_PROMO_CODE || searchCode !== '' ? searchCode : NO_PROMO_CODE;
  pushEvent({
    event: EVENT_CATEGORIES.GTM.checkoutOption,
    ecommerce: {
      checkout_option: {
        actionField: {
          step: 2,
          option: optionCode
        }
      }
    }
  });

  // 3. PuchaseSuite
  const isSeasonPass = reservation.reservationType.includes(RESERVATION_TYPE.SEASONPASS.toUpperCase());
  const seasonPassCode = reservation.product?.code || '';
  const purchaseSuiteObj = {
    event: EVENT_CATEGORIES.GTM.purchaseSuite,
    loggedinStatus: isUserLoggedIn,
    ecommerce: {
      purchase: {
        actionField: {
          id: UUID,
          confirmationNumber: reservation?.id ? reservation.id : reservation?.externalId,
          coupon: isSeasonPass ? seasonPassCode : reservation.offerCode,
          revenue: averageRate.toFixed(2) * (isDayPass ? reservation.guests.quantity : numberOfNights),
          SIP: dayType
        },
        products: [
          {
            name: reservation.product.name,
            id: reservation.reservationType,
            price: averageRate.toFixed(2),
            category: isDayPass ? RESERVATION_TYPE.DAYPASS : suiteCategory,
            variant: 'standard',
            brand: 'great wolf',
            quantity: isDayPass ? reservation.guests.quantity : numberOfNights,
            dimension1: reservation.payment.description,
            coupon: isSeasonPass ? seasonPassCode : reservation.offerCode
          }
        ]
      }
    }
  };

  pushEvent(purchaseSuiteObj);

  const uniquePackages = [...new Map(packages.map(m => [m.id, m])).values()];

  // 4. PurchasePackage
  const purchasePackageObj = {
    event: EVENT_CATEGORIES.GTM.purchasePackage,
    ecommerce: {
      purchase: {
        actionField: {
          id: UUID,
          confirmationNumber: reservation.id ? reservation.id : reservation.externalId,
          coupon: optionCode,
          revenue: packageTotalRevenue.toFixed(2),
          SIP: dayType
        },
        products: uniquePackages
      }
    }
  };

  pushEvent(purchasePackageObj);

  // 5. Package revenue
  pushEvent({
    total_package_revenue: packageTotalRevenue.toFixed(2)
  });

  // 6. Affirm
  try {
    const payload = getAnalyticsPayload(reservation);
    window.affirm?.analytics?.trackOrderConfirmed(payload);
  } catch (error) {
    console.log(error);
  }
};

const pushSuiteCheckoutSuccessData = async (
  reservation,
  packages,
  packagesContent,
  voyagerPointsDiscount,
  offersPointsDiscount,
  dayType,
  suiteContent,
  UUID,
  upsellRecommendationData,
  offerCode
) => {
  const { maxOccupancy, suiteCategory } = suiteContent;
  const products = [];
  const { router, suite, offer, account, dayPasses, cart } = getStore().getState();
  const seasonPassCode = dayPasses?.selectedItemCode || '';
  const isPassReservation = [RESERVATION_TYPE.DAYPASS, RESERVATION_TYPE.SEASONPASS].some(passType =>
    reservation.reservationType.includes(passType.toUpperCase())
  );
  const couponCode = isPassReservation ? '' : offerCode || reservation?.offerCode || reservation?.rateCode || '';
  const currentPathLocation = get(router, 'location.pathname', '');
  const purchaseLocation = getCurrentLocation(currentPathLocation);
  const selectedOffers = get(offer, 'activeOffers', []);
  const userOfferList = get(account, 'currentLoggedInUser.offerList', []);

  let allActiveOffers = [];
  let formatedOffers = [];
  // If we have offer we add the offers to the newAddedPackages
  if (selectedOffers.length > 0 && userOfferList.length > 0) {
    allActiveOffers = selectedOffers.map(offer => {
      const offerFound = userOfferList.find(userOffer => userOffer.id === offer);
      return {
        ...offerFound
      };
    });

    if (allActiveOffers.length > 0) {
      formatedOffers = allActiveOffers.map((offer, index) => {
        return {
          packageName: 'Bonus deal',
          packageCode: `bonusdeal${index}`,
          amount: offer.dollarValue,
          category: 'bonusDeal',
          packageCategory: offer.title,
          quantity: 1
        };
      });
    }
  }

  const suiteProduct = buildSuitePurchaseEnhancedECommerceObject(
    reservation,
    purchaseLocation || PRODUCT_SCOPES.suiteCheckout,
    maxOccupancy,
    suiteCategory,
    upsellRecommendationData,
    seasonPassCode,
    couponCode
  );

  products.push(suiteProduct);

  if (packages) {
    const addedPackagesCodes = getAddedPackagesCodes(packages);
    const groupedAddedPackages = getGroupedAddedPackages({
      addedPackagesCodes,
      reservation
    });

    const packagesProducts = buildPackagesPurchaseEnhancedECommerceObject(
      {
        suite,
        addedPackagesCodes,
        packages,
        groupedAddedPackages,
        reservation,
        packagesContent,
        formatedOffers,
        maxOccupancy,
        upsellRecommendationData,
        couponCode
      },
      purchaseLocation || PRODUCT_SCOPES.suiteCheckout
    );
    products.push(...packagesProducts);
  }
  const purchaseEnhancedECommerceData = await buildPurchaseEnhancedECommerceObject(
    {
      reservation,
      products,
      revenue: reservation.costSummary.total.toFixed(2),
      tax: reservation.costSummary?.taxesAndFees?.TAX?.toFixed(2),
      shipping: (
        (reservation.costSummary?.taxesAndFees?.SUSTAINABILITY ?? 0) +
        (reservation.costSummary?.taxesAndFees?.RESORT ?? 0)
      ).toFixed(2),
      voyagerPointsDiscount,
      offersPointsDiscount,
      dayType,
      UUID,
      upsellRecommendationData,
      seasonPassCode,
      couponCode,
      handoffCartId: isPassReservation ? '' : getHandoffCartId(cart ?? {}),
      appliedViaPromoBanner: getIsOfferAppliedViaPromoBanner(offer ?? {})
    },
    EVENT_CATEGORIES.GTM.transaction,
    purchaseLocation || PRODUCT_SCOPES.suiteCheckout
  );

  pushEvent(purchaseEnhancedECommerceData);
};

const pushEitherSuiteOrDayPass = (reservation, suiteCategory) => {
  // get currency code
  const currencyCode = getReservationCurrency(reservation.lodge.code);

  const [locationDisplayName, locationState] = getLodgeDataByCode(reservation?.lodge?.code)?.display?.split(', ') ?? [];

  const isDayPass = reservation.reservationType.includes('PASS');

  const baseObject = getEventBaseObject(EVENT_CATEGORIES.GTM.fbPurchase, reservation, currencyCode);
  const additionalObject = !isDayPass
    ? getBaseSuiteObject(reservation, locationDisplayName, locationState, suiteCategory)
    : getDayPassBaseObject(reservation, locationDisplayName, locationState);

  pushEvent({
    ...baseObject,
    ...additionalObject
  });
};

/**
 * Listener on purchase event
 * @param {Object} event
 */
export const onPackagesUpsert = event => {
  // load state contains the historic of the state
  // this event is triggered before it reaches to the reducer so we
  // load the prev saved state
  const { packages: packagesOld, guests, dates, currentLodge, router, suite, plans, entities } = getStore().getState();
  const selectedPackages = get(event, 'selectedPackages', []);
  const isCartVariation = get(event, 'isCartVariation', false);
  const selectedPackagesOld = get(packagesOld, 'selectedPackages', []);
  const packagesTable = get(packagesOld, 'planPackages', []);
  const currentPathLocation = get(router, 'location.pathname', '');
  const upsellRecommendationData = plans?.upsellRecommendationData;
  const resortLocation = getResortLocation(currentLodge);
  const isCartOpenClose = plans.isCartOpenClose;

  const isBday = get(event, 'isBday', false);
  const partyDate = get(event, 'partyDate', 0);
  const partyAdults = get(event, 'partyAdults', 0);
  const partyKids = get(event, 'partyKids', 0);
  const partyInfants = get(event, 'partyInfants', 0);
  const partySpectators = get(event, 'partySpectators', 0);
  const partyTime = get(event, 'partyTime', '');
  const recommendationVariant = get(event, 'recommendationVariant', 'other');
  const isDaypass = getIsDaypass(entities.manageReservationDetails);
  let purchaseLocation = getCurrentLocation(currentPathLocation, isDaypass);
  if (isCartVariation) {
    purchaseLocation = CUSTOM_PURCHASE_LOCATIONS.cart;
  }

  if (!selectedPackages.length > 0) return;

  //Find values that are in selectedPackages but not in selectedPackagesOld
  let newAddedPackages = findElementsNotInList(selectedPackages, selectedPackagesOld, 'packageCode');

  // NOTE: Package completely removed -- this case is handled on this method: onPackageDelete
  // A. PACKAGE ADDITION
  // 1. New added package, new Added Packages comes with an object
  if (newAddedPackages && newAddedPackages.length > 0) {
    // Perform added package
    let eventObject = buildPackageCartEventObject(
      EVENT_CATEGORIES.GTM.addToCart,
      newAddedPackages,
      packagesTable,
      guests,
      dates,
      resortLocation,
      null,
      purchaseLocation,
      currentPathLocation,
      suite,
      isCartOpenClose,
      upsellRecommendationData,
      entities,
      recommendationVariant
    );

    if (isBday) {
      const { title, packageCode, amount } = getTheFirstElementOfArray(newAddedPackages);
      eventObject = getBirthdayPartySelectObject(
        EVENT_CATEGORIES.GTM.addToCart,
        resortLocation,
        partyDate,
        partyAdults,
        partyKids,
        partyInfants,
        partySpectators,
        partyTime,
        title,
        packageCode,
        amount
      );
    }

    pushEvent(eventObject);
  }
  // historic and new state. But the event was triggered, need to check the quantity diff
  else {
    selectedPackagesOld.forEach(pkgOld => {
      const pkg = selectedPackages.find(pkg => pkg.packageCode === pkgOld.packageCode);

      const qtyDiff = pkg ? pkg.quantity - pkgOld.quantity : 0;
      // if there is a difference between the historic and new information then we should track it
      // else ignore it
      if (qtyDiff !== 0) {
        // 2. Package already added but quantity added. Quanity is positive due the incresed difference
        let eventTag = EVENT_CATEGORIES.GTM.addToCart;

        // B. PACKAGE REMOVAL
        // 3. Package already added but quantity decreased. Quantity is negative due the decrese difference
        if (qtyDiff < 0) {
          // Perform removed package
          eventTag = EVENT_CATEGORIES.GTM.removeFromCart;
        }

        let eventObject = buildPackageCartEventObject(
          eventTag,
          [pkg],
          packagesTable,
          guests,
          dates,
          resortLocation,
          Math.abs(qtyDiff),
          purchaseLocation,
          currentPathLocation,
          suite,
          isCartOpenClose,
          upsellRecommendationData,
          entities
        );

        if (isBday) {
          eventObject = getBirthdayPartySelectObject(
            eventTag,
            resortLocation,
            partyDate,
            partyAdults,
            partyKids,
            partyInfants,
            partySpectators,
            partyTime,
            pkgOld.title,
            pkgOld.packageCode,
            pkgOld.amount
          );
        }

        pushEvent(eventObject);
      }
    });
  }

  // In order to save this event in the history (so we can log it) we should return it!
  // otherwise it will be ignored!
  return event;
};
onPackagesUpsert.eventType = packagesActionTypes.UPSERT_PACKAGES; // Redux Action Type. Get it from the store

export const onPackageDelete = event => {
  // load state contains the historic of the state
  // this event is triggered before it reaches to the reducer so we
  // load the prev saved state
  const { packages: packagesOld, guests, dates, currentLodge, router, suite, plans, entities } = getStore().getState();
  const selectedPackages = get(event, 'selectedPackages', []);
  const selectedPackagesOld = get(packagesOld, 'selectedPackages', []);
  const packagesTable = get(packagesOld, 'planPackages', []);
  const upsellRecommendationData = plans?.upsellRecommendationData;
  const currentPathLocation = get(router, 'location.pathname', '');
  const isDaypass = getIsDaypass(entities.manageReservationDetails);
  const purchaseLocation = getCurrentLocation(currentPathLocation, isDaypass);
  const resortLocation = getResortLocation(currentLodge);
  const isCartOpenClose = plans.isCartOpenClose;

  const isBday = get(event, 'isBday', false);
  const partyDate = get(event, 'partyDate', 0);
  const partyAdults = get(event, 'partyAdults', 0);
  const partyKids = get(event, 'partyKids', 0);
  const partyInfants = get(event, 'partyInfants', 0);
  const partySpectators = get(event, 'partySpectators', 0);
  const partyTime = get(event, 'partyTime', '');

  if (!selectedPackagesOld.length > 0) return;

  let removedPackages = findElementsNotInList(selectedPackagesOld, selectedPackages, 'packageCode');

  if (removedPackages && removedPackages.length > 0) {
    // Perform added package
    let eventObject = buildPackageCartEventObject(
      EVENT_CATEGORIES.GTM.removeFromCart,
      removedPackages,
      packagesTable,
      guests,
      dates,
      resortLocation,
      null,
      purchaseLocation,
      currentPathLocation,
      suite,
      isCartOpenClose,
      upsellRecommendationData,
      entities
    );

    if (isBday) {
      const { title, packageCode, amount } = getTheFirstElementOfArray(removedPackages);
      eventObject = getBirthdayPartySelectObject(
        EVENT_CATEGORIES.GTM.removeFromCart,
        resortLocation,
        partyDate,
        partyAdults,
        partyKids,
        partyInfants,
        partySpectators,
        partyTime,
        title,
        packageCode,
        amount
      );
    }

    pushEvent(eventObject);
  }

  // In order to save this event in the history (so we can log it) we should return it!
  // otherwise it will be ignored!
  return event;
};
onPackageDelete.eventType = packagesActionTypes.DELETE_PACKAGES; // Redux Action Type. Get it from the store

export const onOfferAdded = event => {
  // load state contains the historic of the state
  // this event is triggered before it reaches to the reducer so we
  // load the prev saved state
  const { guests, dates, currentLodge, router, account, suite, plans } = getStore().getState();
  const currentPathLocation = get(router, 'location.pathname', '');
  const purchaseLocation = getCurrentLocation(currentPathLocation);
  const upsellRecommendationData = plans?.upsellRecommendationData;
  const resortLocation = getResortLocation(currentLodge);
  const selectedOffer = event.offerId;
  const userOfferList = get(account, 'currentLoggedInUser.offerList', []);
  const isCartOpenClose = plans.isCartOpenClose;
  let addedOffers = [];

  // If we have offer we add the offers to the newAddedPackages
  if (selectedOffer) {
    const activeOffer = userOfferList.filter(offer => offer.id === selectedOffer);

    if (activeOffer) {
      const formatedOffers = activeOffer.map((offer, index) => {
        return {
          packageName: 'Bonus deal',
          packageCode: `bonusdeal${index}`,
          amount: offer.dollarValue,
          category: 'bonusDeal',
          packageCategory: offer.title,
          quantity: 1
        };
      });
      addedOffers = [...formatedOffers];
    }
  }

  if (addedOffers && addedOffers.length > 0) {
    // Perform added package
    const eventObject = buildPackageCartEventObject(
      EVENT_CATEGORIES.GTM.addToCart,
      addedOffers,
      null,
      guests,
      dates,
      resortLocation,
      null,
      purchaseLocation,
      currentPathLocation,
      suite,
      isCartOpenClose,
      upsellRecommendationData
    );

    pushEvent(eventObject);
  }
};

onOfferAdded.eventType = offerActionTypes.ADD_ACTIVE_OFFER; // Redux Action Type. Get it from the store

export const onOfferRemoved = event => {
  // load state contains the historic of the state
  // this event is triggered before it reaches to the reducer so we
  // load the prev saved state
  const { guests, dates, currentLodge, router, account, suite, plans } = getStore().getState();
  const currentPathLocation = get(router, 'location.pathname', '');
  const purchaseLocation = getCurrentLocation(currentPathLocation);
  const resortLocation = getResortLocation(currentLodge);
  const upsellRecommendationData = plans?.upsellRecommendationData;
  const selectedOffer = event.offerId;
  const userOfferList = get(account, 'currentLoggedInUser.offerList', []);
  const isCartOpenClose = plans.isCartOpenClose;

  let addedOffers = [];

  // If we have offer we add the offers to the newAddedPackages
  if (selectedOffer) {
    const activeOffer = userOfferList.filter(offer => offer.id === selectedOffer);

    if (activeOffer) {
      const formatedOffers = activeOffer.map((offer, index) => {
        return {
          packageName: 'Bonus deal',
          packageCode: `bonusdeal${index}`,
          amount: offer.dollarValue,
          category: 'bonusDeal',
          packageCategory: offer.title,
          quantity: 1
        };
      });
      addedOffers = [...formatedOffers];
    }
  }

  if (addedOffers && addedOffers.length > 0) {
    // Perform added package
    const eventObject = buildPackageCartEventObject(
      EVENT_CATEGORIES.GTM.removeFromCart,
      addedOffers,
      null,
      guests,
      dates,
      resortLocation,
      null,
      purchaseLocation,
      currentPathLocation,
      suite,
      isCartOpenClose,
      upsellRecommendationData
    );

    pushEvent(eventObject);
  }
};

onOfferRemoved.eventType = offerActionTypes.REMOVE_ACTIVE_OFFER; // Redux Action Type. Get it from the store
